import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createContext } from 'react';

import { SnapshotTypeEnum } from '../model/snapshot_type';
import Utils from '../utils/immersive_utils';

import { SnapshotRequest } from './model/snapshotData';
import { OverviewContext } from './overview-provider';
import { WebSocketContext } from './websocket-context/websocket-provider';

interface IParamsProvider {
  children: ReactNode;
  providerKey?: string;
}

interface IProvider {
  volData: any;
  loading: boolean;
  setLoading: (value: boolean) => void;
  getData: (timestamp?: string) => void;
  unsubscribe: (key: string) => void;
  type: MutableRefObject<SnapshotTypeEnum>;
}

const KEY = 'VOL_SMILE_UPDATE_DATA';

export const VolMarketContext = createContext<IProvider>({} as IProvider);

export const VolMarketProvider = ({ children, providerKey }: IParamsProvider) => {
  const { currency } = useContext(OverviewContext);

  const {
    subscribeToChannel,
    unsubscribeFromChannel: unsubscribe,
    isConnected,
    sendMessage,
  } = useContext(WebSocketContext);
  const [socketEvent, setSocketEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const generateCompositeId = (id: string) => {
    return Utils.generateCompositeIdWithPrefix(id, currency, providerKey);
  };

  const type = useRef<SnapshotTypeEnum>(SnapshotTypeEnum.CM);

  const handleResponse = useCallback(
    (param: any) => {
      const { data } = param?.params ?? {};

      setSocketEvent(data);
    },
    [setSocketEvent],
  );

  const { label } = useContext(OverviewContext);

  const getData = useCallback(() => {
    const req = new SnapshotRequest(
      generateCompositeId(KEY),
      currency,
      type.current,
      new Date().toISOString(),
    );

    sendMessage(JSON.stringify(req));
    subscribeToChannel(generateCompositeId(KEY), handleResponse);
  }, [currency, sendMessage]);

  const handleUnsubscribe = useCallback(() => {
    unsubscribe(generateCompositeId(KEY));
  }, []);

  const handleChangeCurrency = useCallback(() => {
    if (isConnected) {
      setLoading(true);

      handleUnsubscribe();

      getData();
    }
  }, [getData, isConnected, handleUnsubscribe]);

  useEffect(() => {
    handleChangeCurrency();
  }, [currency, label, isConnected]);

  return (
    <VolMarketContext.Provider
      value={{
        volData: socketEvent,
        loading,
        setLoading,
        getData,
        unsubscribe,
        type,
      }}
    >
      {children}
    </VolMarketContext.Provider>
  );
};
